import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Crown, Star, BookOpen, Eye, ArrowLeft, Sparkles } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useSubscription } from '../context/SubscriptionContext';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TopStories = () => {
  const navigate = useNavigate();
  const { isPremium } = useSubscription();
  const { lang } = useLanguage();
  const [stories, setStories] = useState([]);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopStories();
  }, [lang]);

  const fetchTopStories = async () => {
    try {
      const response = await axios.get(`${API}/stories/top?limit=10&lang=${lang}`);
      setStories(response.data);
      // Fetch ratings for each story
      const ratingPromises = response.data.map(s =>
        axios.get(`${API}/reviews/${s.id}`).then(r => ({ id: s.id, ...r.data })).catch(() => ({ id: s.id, average_rating: 0, total: 0 }))
      );
      const allRatings = await Promise.all(ratingPromises);
      const ratingMap = {};
      allRatings.forEach(r => { ratingMap[r.id] = { avg: r.average_rating, total: r.total }; });
      setRatings(ratingMap);
    } catch (error) {
      console.error('Failed to fetch top stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-8 h-8 text-yellow-400" />;
    if (rank === 2) return <Trophy className="w-7 h-7 text-gray-400" />;
    if (rank === 3) return <Trophy className="w-6 h-6 text-amber-600" />;
    return <span className="font-display text-2xl text-muted-foreground">#{rank}</span>;
  };

  const getRankBgClass = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400/20 to-amber-400/20 border-yellow-400/50';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300/20 to-gray-400/20 border-gray-400/50';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-orange-500/20 border-amber-600/50';
    return 'bg-card/50 border-border/50';
  };

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="top-stories-page">
      <SEO path="/top" />
      {/* Back Button */}
      <div className="max-w-4xl mx-auto mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="gap-2 text-muted-foreground hover:text-foreground"
          data-testid="back-to-library-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Terug naar bibliotheek
        </Button>
      </div>

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <div className="flex justify-center gap-2 mb-4">
          <motion.div
            animate={{ rotate: [-10, 10, -10] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star className="w-8 h-8 text-gold fill-gold" />
          </motion.div>
          <Trophy className="w-12 h-12 text-gold" />
          <motion.div
            animate={{ rotate: [10, -10, 10] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star className="w-8 h-8 text-gold fill-gold" />
          </motion.div>
        </div>
        <h1 className="font-display text-4xl md:text-6xl text-foreground mb-4">
          Top 10 Verhalen
        </h1>
        <p className="text-muted-foreground text-lg font-accent max-w-md mx-auto">
          De meest geliefde verhalen van Oma's Verhaaltjes
        </p>
      </motion.div>

      {/* Stories List */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-2xl" />
            ))}
          </div>
        ) : stories.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16 bg-card/30 rounded-3xl border border-border/50"
          >
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-display text-2xl text-foreground mb-2">
              Nog geen verhalen
            </h3>
            <p className="text-muted-foreground font-accent mb-6">
              De ranglijst wordt gevuld zodra verhalen worden gelezen
            </p>
            <Button
              onClick={() => navigate('/')}
              className="rounded-full bg-gold text-night-blue hover:bg-gold/90"
            >
              Bekijk alle verhalen
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {stories.map((story, index) => {
              const rank = index + 1;
              const isLocked = !story.is_free && !isPremium;
              
              return (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative rounded-2xl border-2 overflow-hidden ${getRankBgClass(rank)} 
                    ${isLocked ? 'opacity-80' : 'cursor-pointer hover:scale-[1.02]'} transition-all duration-300`}
                  onClick={() => !isLocked && navigate(`/story/${story.id}`)}
                  data-testid={`top-story-${rank}`}
                >
                  <div className="flex items-center gap-4 p-4 md:p-6">
                    {/* Rank Badge */}
                    <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                      {getRankIcon(rank)}
                    </div>

                    {/* Cover Image */}
                    <div className="flex-shrink-0 w-20 h-28 md:w-24 md:h-32 rounded-lg overflow-hidden shadow-lg">
                      {story.cover_image_url ? (
                        <img
                          src={story.cover_image_url.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${story.cover_image_url}` 
                            : story.cover_image_url}
                          alt={story.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-old-rose/30 to-gold/30 flex items-center justify-center">
                          <BookOpen className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Story Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display text-xl md:text-2xl text-foreground truncate">
                          {story.title}
                        </h3>
                        {!story.is_free && (
                          <Crown className="w-5 h-5 text-gold flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-muted-foreground font-accent text-sm mb-2">
                        door {story.grandma_name}
                      </p>
                      <p className="text-muted-foreground text-sm line-clamp-2 hidden md:block">
                        {story.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{story.read_count.toLocaleString('nl-NL')} keer gelezen</span>
                        </div>
                        {ratings[story.id]?.avg > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(ratings[story.id].avg) ? 'text-gold fill-gold' : 'text-muted-foreground/30'}`} />
                              ))}
                            </div>
                            <span className="text-xs">({ratings[story.id].total})</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Lock Overlay */}
                    {isLocked && (
                      <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] flex items-center justify-center">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/subscription');
                          }}
                          className="rounded-full bg-gold text-night-blue hover:bg-gold/90 gap-2"
                          data-testid={`unlock-story-${rank}-btn`}
                        >
                          <Crown className="w-4 h-4" />
                          Ontgrendelen
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Call to Action */}
        {!loading && stories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="text-center mt-12"
          >
            <p className="text-muted-foreground font-accent mb-4">
              Wil je meer verhalen ontdekken?
            </p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full border-gold/50 text-gold hover:bg-gold/10"
            >
              Bekijk alle verhalen
            </Button>
          </motion.div>
        )}

        {/* Decorative Stars */}
        <div className="flex justify-center gap-4 mt-12">
          <motion.div 
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star className="w-6 h-6 text-gold fill-gold" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
          >
            <Sparkles className="w-5 h-5 text-gold" />
          </motion.div>
          <motion.div 
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
          >
            <Star className="w-6 h-6 text-gold fill-gold" />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TopStories;
